import { Injectable, NgZone, PLATFORM_ID, Signal, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { filter } from 'rxjs/operators';
import { AiAgentService } from './API/ai-agent.service';
import { UnifiedAuthService } from './unified-auth.service';
import { resolveActiveSubPerfil } from '../component/mini-subheader/subperfil-match';

/** Sugestão de ajuda contextual disparada por inatividade na tela atual. */
export interface SugestaoAjuda {
  rota: string;
  titulo: string;
}

const IDLE_MS = 2 * 60 * 1000;
const EVENTOS_ATIVIDADE = ['mousemove', 'keydown', 'scroll', 'touchstart'] as const;

/**
 * Mede inatividade por rota e sugere ajuda do assistente ("Precisa de ajuda com esta tela?").
 * 1 disparo por visita à rota; PDV/admin nunca; supressões re-armam sem consumir a visita.
 * Listeners ficam fora da zona do Angular — mousemove não pode gerar change detection.
 */
@Injectable({ providedIn: 'root' })
export class AiIdleHelpService {
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly svc = inject(AiAgentService);
  private readonly auth = inject(UnifiedAuthService);
  private readonly dialog = inject(MatDialog);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly _sugestaoAjuda = signal<SugestaoAjuda | null>(null);
  readonly sugestaoAjuda: Signal<SugestaoAjuda | null> = this._sugestaoAjuda.asReadonly();

  private iniciado = false;
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private ultimaAtividade = 0;
  private rotaAtual = '';
  /** true depois de disparar ou dispensar nesta visita à rota. */
  private consumido = false;

  /** Liga o monitor. No-op fora do browser e em chamadas repetidas. */
  iniciar(): void {
    if (this.iniciado || !isPlatformBrowser(this.platformId)) return;
    this.iniciado = true;

    this.rotaAtual = this.router.url;
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.aoNavegar(e.urlAfterRedirects || e.url));

    this.zone.runOutsideAngular(() => {
      for (const nome of EVENTOS_ATIVIDADE) {
        document.addEventListener(nome, this.aoInteragir, { passive: true, capture: true });
      }
      document.addEventListener('visibilitychange', this.aoMudarVisibilidade);
    });

    this.armar();
  }

  /** Fecha o balão; não re-oferece até nova visita à rota. */
  dispensar(): void {
    this._sugestaoAjuda.set(null);
    this.consumido = true;
    this.limparTimer();
  }

  /** Consome a sugestão atual (clique no balão). */
  consumir(): SugestaoAjuda | null {
    const s = this._sugestaoAjuda();
    this.dispensar();
    return s;
  }

  private readonly aoInteragir = (): void => {
    this.ultimaAtividade = Date.now();
  };

  private readonly aoMudarVisibilidade = (): void => {
    if (document.visibilityState === 'hidden') {
      this.limparTimer();
    } else if (!this.consumido) {
      this.armar();
    }
  };

  private aoNavegar(url: string): void {
    this.rotaAtual = url;
    this.consumido = false;
    // Navegar mata balão pendente da tela anterior — a sugestão é sobre A tela atual.
    this._sugestaoAjuda.set(null);
    this.armar();
  }

  private armar(): void {
    this.limparTimer();
    if (this.consumido || !this.rotaElegivel(this.rotaAtual) || !this.temUsuario()) return;
    this.ultimaAtividade = Date.now();
    this.agendar(IDLE_MS);
  }

  private agendar(ms: number): void {
    this.zone.runOutsideAngular(() => {
      this.timerId = setTimeout(() => this.aoCompletarTimer(), ms);
    });
  }

  private aoCompletarTimer(): void {
    this.timerId = null;
    // Interação move ultimaAtividade sem mexer no timeout (barato p/ mousemove);
    // aqui só re-agenda o tempo que falta desde a última interação.
    const decorrido = Date.now() - this.ultimaAtividade;
    if (decorrido < IDLE_MS) {
      this.agendar(IDLE_MS - decorrido);
      return;
    }
    if (this.suprimido()) {
      // Não consome a visita: tenta de novo dali a IDLE_MS.
      this.ultimaAtividade = Date.now();
      this.agendar(IDLE_MS);
      return;
    }
    this.consumido = true;
    const rota = this.rotaAtual;
    const titulo = this.tituloDaTela(rota);
    this.zone.run(() => this._sugestaoAjuda.set({ rota, titulo }));
  }

  private suprimido(): boolean {
    return this.svc.aberto()
      || this.svc.naoLidos() > 0
      || !this.temUsuario()
      || this.dialog.openDialogs.length > 0
      || document.visibilityState === 'hidden'
      || !this.rotaElegivel(this.rotaAtual);
  }

  private temUsuario(): boolean {
    const u = this.auth.getUserInfos() as { token?: string } | null;
    return !!u?.token;
  }

  private rotaElegivel(url: string): boolean {
    const path = url.split('?')[0];
    if (path.startsWith('/pdv') || path.startsWith('/admin')) return false;
    const host = window.location.hostname;
    if (host.startsWith('pdv.') || host.startsWith('admin.')) return false;
    return true;
  }

  private tituloDaTela(url: string): string {
    const path = url.split('?')[0];
    try {
      const u = this.auth.getUserInfos() as {
        usuarioPermissionStruct?: { listSubPerfilStruct?: unknown[] };
      } | null;
      const lista = (u?.usuarioPermissionStruct?.listSubPerfilStruct ?? []) as Parameters<
        typeof resolveActiveSubPerfil
      >[1];
      const m = resolveActiveSubPerfil(url, lista);
      return m?.nomeSubPerfil || path;
    } catch {
      // getUserInfos()/resolveActiveSubPerfil malformado nunca deve quebrar o disparo do balão.
      return path;
    }
  }

  private limparTimer(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }
}
